import React from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { themes, hexToRgba } from '../data/themes';

const parseLeadValue = (valStr) => {
  if (!valStr) return 0;
  return parseFloat(valStr.replace(/[^0-9,-]/g, '').replace(',', '.'));
};

const DashboardPage = ({ active, leads, tasks, dashFilter, setDashFilter, onDownloadReport, themeId }) => {
  const filteredDashboardLeads = leads.filter(l => {
    return (!dashFilter.start || l.date >= dashFilter.start) && (!dashFilter.end || l.date <= dashFilter.end);
  });

  const activeLeads = filteredDashboardLeads.filter(l => ['new', 'meeting', 'nego'].includes(l.col));
  const wonLeads = filteredDashboardLeads.filter(l => l.col === 'won');
  const lostLeads = filteredDashboardLeads.filter(l => l.col === 'lost');

  const pipelineValue = activeLeads.reduce((sum, l) => sum + parseLeadValue(l.value), 0);
  const wonValue = wonLeads.reduce((sum, l) => sum + parseLeadValue(l.value), 0);
  const avgDealValue = activeLeads.length > 0 ? (pipelineValue / activeLeads.length) : 0;

  const totalClosed = wonLeads.length + lostLeads.length;
  const winRate = totalClosed > 0 ? Math.round((wonLeads.length / totalClosed) * 100) : 0;
  const lossRate = totalClosed > 0 ? 100 - winRate : 0;

  const countNew = filteredDashboardLeads.filter(l => l.col === 'new').length;
  const countMeeting = filteredDashboardLeads.filter(l => l.col === 'meeting').length;
  const countNego = filteredDashboardLeads.filter(l => l.col === 'nego').length;
  const countWon = wonLeads.length;
  const maxStageCount = Math.max(countNew, countMeeting, countNego, countWon, 1);

  const currentThemeColors = themes.find(t => t.id === themeId) || themes[0];

  const winLossData = {
    labels: ['Win', 'Loss'],
    datasets: [{
      data: totalClosed > 0 ? [winRate, lossRate] : [1, 1],
      backgroundColor: ['rgba(39,174,96,0.7)', 'rgba(192,57,43,0.5)'],
      borderColor: ['rgba(39,174,96,0.9)', 'rgba(192,57,43,0.8)'],
      borderWidth: 1
    }]
  };

  const revenueData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Closed Revenue',
        data: [320, 480, 410, 590, 520, 780],
        borderColor: currentThemeColors.accent,
        backgroundColor: hexToRgba(currentThemeColors.accent, 0.15) || 'rgba(212,175,55,0.15)',
        borderWidth: 2, fill: true, tension: 0.4,
        pointBackgroundColor: currentThemeColors.accent, pointRadius: 4
      },
      {
        label: 'Pipeline Value',
        data: [520, 680, 700, 820, 760, 940],
        borderColor: '#2980B9',
        backgroundColor: 'rgba(41,128,185,0.08)',
        borderWidth: 2, fill: true, tension: 0.4,
        pointBackgroundColor: '#2980B9', pointRadius: 4
      }
    ]
  };

  const lineOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { color: 'rgba(128,128,128,0.1)' } },
      y: { grid: { color: 'rgba(128,128,128,0.1)' }, ticks: { callback: v => 'Rp ' + v + 'M' } }
    },
    interaction: { mode: 'index', intersect: false }
  };

  return (
    <section className={`section ${active ? 'active' : ''}`} data-testid="dashboard-section">
      <div className="section-header">
        <div>
          <div className="section-title">Overview</div>
          <div className="section-subtitle">Real-time performance metrics for your pipeline</div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--glass)', padding: '4px 12px', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Dari:</span>
            <input type="date" value={dashFilter.start}
              onChange={e => setDashFilter({ ...dashFilter, start: e.target.value })}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none', fontFamily: "'JetBrains Mono'", fontSize: '11px' }}
              data-testid="dash-filter-start" />
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Sampai:</span>
            <input type="date" value={dashFilter.end}
              onChange={e => setDashFilter({ ...dashFilter, end: e.target.value })}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none', fontFamily: "'JetBrains Mono'", fontSize: '11px' }}
              data-testid="dash-filter-end" />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onDownloadReport} data-testid="download-report-btn">Download Report ⤓</button>
        </div>
      </div>

      <div className="bento-grid">
        <div className="bento-card col-7" style={{ background: 'linear-gradient(135deg, var(--gold-glow), var(--black))' }}>
          <div className="card-label">Total Revenue Pipeline</div>
          <div className="pipeline-total">
            <div className="curr">Rp</div>
            <div className="big glow-gold" data-testid="pipeline-total">{pipelineValue.toFixed(1).replace('.', ',')}M</div>
          </div>
          <div className="gold-divider"></div>
          <div className="card-change up"><span className="trend-up"></span>Filtered by Date Range</div>
          <div style={{ marginTop: '20px' }}>
            <div className="stat-bar">
              <div className="stat-bar-label"><span>Target Q2</span><span>Rp 92M</span></div>
              <div className="stat-bar-track"><div className="stat-bar-fill" style={{ width: '80%' }}></div></div>
            </div>
            <div className="stat-bar" style={{ marginTop: '10px' }}>
              <div className="stat-bar-label"><span>Closed Won</span><span>Rp {wonValue.toFixed(1).replace('.', ',')}M</span></div>
              <div className="stat-bar-track"><div className="stat-bar-fill" style={{ width: `${Math.min((wonValue / 92) * 100, 100)}%`, background: 'linear-gradient(90deg, #27AE60, #2ECC71)' }}></div></div>
            </div>
          </div>
        </div>

        <div className="bento-card col-5">
          <div className="card-label">Win / Loss Rate</div>
          <div className="chart-container-sm">
            <Doughnut data={winLossData} options={{ cutout: '72%', plugins: { legend: { display: false }, tooltip: { enabled: false } }, animation: false }} />
          </div>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '8px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#27AE60' }} data-testid="win-rate">{winRate}%</div>
              <div style={{ fontSize: '9px', letterSpacing: '2px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono'" }}>WIN</div>
            </div>
            <div style={{ width: '1px', background: 'var(--glass-border)' }}></div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#C0392B' }} data-testid="loss-rate">{lossRate}%</div>
              <div style={{ fontSize: '9px', letterSpacing: '2px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono'" }}>LOSS</div>
            </div>
          </div>
        </div>

        <div className="bento-card col-3">
          <div className="card-label">Active Leads</div>
          <div className="card-value"><span data-testid="active-leads-count">{activeLeads.length}</span></div>
          <div className="gold-divider"></div>
          <div className="card-change up"><span className="trend-up"></span>Live Pipeline</div>
        </div>
        <div className="bento-card col-3">
          <div className="card-label">Rata-rata Deal</div>
          <div className="card-value">Rp <span>{avgDealValue.toFixed(2).replace('.', ',')}M</span></div>
          <div className="gold-divider"></div>
          <div className="card-change up"><span className="trend-up"></span>Based on filter</div>
        </div>
        <div className="bento-card col-3">
          <div className="card-label">Meeting Terjadwal</div>
          <div className="card-value"><span>{tasks.filter(t => t.tag === 'Meeting').length + countMeeting}</span></div>
          <div className="gold-divider"></div>
          <div className="card-change up"><span className="trend-up"></span>Total Meetings</div>
        </div>
        <div className="bento-card col-3">
          <div className="card-label">Deals Ditutup</div>
          <div className="card-value"><span>{countWon}</span></div>
          <div className="gold-divider"></div>
          <div className="card-change down"><span className="trend-down"></span>Closed Won filter</div>
        </div>

        <div className="bento-card col-8">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div>
              <div className="card-label">Revenue Trend</div>
              <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text-primary)' }}>6-Month Performance</div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '10px', color: 'var(--gold)', background: 'var(--gold-glow)', padding: '4px 10px', borderRadius: '20px', fontFamily: "'JetBrains Mono'" }}>Closed</span>
              <span style={{ fontSize: '10px', color: '#5DADE2', background: 'rgba(41,128,185,0.1)', padding: '4px 10px', borderRadius: '20px', fontFamily: "'JetBrains Mono'" }}>Pipeline</span>
            </div>
          </div>
          <div className="chart-container"><Line data={revenueData} options={lineOptions} /></div>
        </div>

        <div className="bento-card col-4">
          <div className="card-label">Stage Distribution</div>
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>New Prospect</span>
                <span style={{ fontFamily: "'JetBrains Mono'", fontSize: '11px', color: 'var(--text-primary)' }}>{countNew}</span>
              </div>
              <div className="stat-bar-track"><div className="stat-bar-fill" style={{ width: `${(countNew / maxStageCount) * 100}%`, background: 'linear-gradient(90deg,#3A5080,#5A7DC0)' }}></div></div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Meeting Scheduled</span>
                <span style={{ fontFamily: "'JetBrains Mono'", fontSize: '11px', color: 'var(--text-primary)' }}>{countMeeting}</span>
              </div>
              <div className="stat-bar-track"><div className="stat-bar-fill" style={{ width: `${(countMeeting / maxStageCount) * 100}%` }}></div></div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Negotiation</span>
                <span style={{ fontFamily: "'JetBrains Mono'", fontSize: '11px', color: 'var(--text-primary)' }}>{countNego}</span>
              </div>
              <div className="stat-bar-track"><div className="stat-bar-fill" style={{ width: `${(countNego / maxStageCount) * 100}%`, background: 'linear-gradient(90deg,#8A6010,var(--gold))' }}></div></div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Closed Won</span>
                <span style={{ fontFamily: "'JetBrains Mono'", fontSize: '11px', color: 'var(--text-primary)' }}>{countWon}</span>
              </div>
              <div className="stat-bar-track"><div className="stat-bar-fill" style={{ width: `${(countWon / maxStageCount) * 100}%`, background: 'linear-gradient(90deg,#1A7040,#27AE60)' }}></div></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPage;
